import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackPageConversion } from '../lib/analytics';
import { ArrowRight, Info, ExternalLink } from 'lucide-react';

interface FeaturedLoan {
  id: string;
  bank_id: string;
  bank_name: string;
  bank_logo: string;
  product_name: string;
  loan_type: string;
  description: string;
  interest_rate: string;
  min_amount: string;
  max_amount: string;
  min_term: string;
  max_term: string;
  features: string[];
  campaign_title?: string;
  campaign_description?: string;
  redirect_url: string;
  is_featured: boolean;
  is_active: boolean;
}

const SponsoredBanks = () => {
  const location = useLocation();
  const [featuredLoans, setFeaturedLoans] = useState<FeaturedLoan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [gtmClasses, setGtmClasses] = useState<Record<string, string>>({});

  const getLoanTypeFromPath = () => {
    const path = location.pathname.substring(1);
    return path || 'ihtiyac-kredisi';
  };

  useEffect(() => {
    fetchFeaturedLoans();
    fetchGTMClasses();
  }, [location.pathname]);

  const fetchGTMClasses = async () => {
    try {
      const pageSlug = location.pathname;
      const { data, error } = await supabase
        .from('page_conversion_tracking')
        .select('gtm_classes, enable_gtm')
        .eq('page_slug', pageSlug)
        .maybeSingle();

      if (!error && data && data.enable_gtm) {
        setGtmClasses(data.gtm_classes || {});
      }
    } catch (error) {
      console.error('Error fetching GTM classes:', error);
    }
  };

  const fetchFeaturedLoans = async () => {
    try {
      setIsLoading(true);
      const loanType = getLoanTypeFromPath();

      const { data, error } = await supabase
        .from('featured_loans')
        .select('*')
        .eq('loan_type', loanType)
        .eq('is_featured', true)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching featured loans:', error);
        return;
      }

      setFeaturedLoans(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F7BA45] mx-auto mb-2"></div>
          <p className="text-sm text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const pushGTMEvent = (bankName: string, bankId: string, loanType: string) => {
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'bank_button_click',
        bank_name: bankName,
        bank_id: bankId,
        page: location.pathname,
        loan_type: loanType,
        button_type: 'sponsored_loan'
      });
      console.log('GTM Event pushed:', { event: 'bank_button_click', bank_name: bankName, bank_id: bankId, loan_type: loanType });
    }
  };

  const handleApply = (loan: FeaturedLoan) => {
    pushGTMEvent(loan.bank_name, loan.bank_id, loan.loan_type);
    trackPageConversion(location.pathname);
  };

  if (featuredLoans.length === 0) {
    return null;
  }
  return (
    <div className="space-y-4">
      {featuredLoans.map((loan) => (
        <div
          key={loan.id}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
        >
          <div className="p-4 md:p-6">
            {/* Bank Header */}
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div className="flex items-center gap-3 md:gap-4">
                <img
                  src={loan.bank_logo}
                  alt={loan.bank_name}
                  className="h-8 md:h-12 object-contain"
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg md:text-xl font-semibold text-slate-800">{loan.bank_name}</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45] text-white text-xs font-medium">
                      Öne Çıkan
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-slate-600">{loan.product_name}</p>
                </div>
              </div>
            </div>

            {/* Product Description */}
            <div className="mb-4">
              <p className="text-sm text-slate-700">{loan.description}</p>
              {loan.campaign_title && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="font-medium text-green-800 text-sm">{loan.campaign_title}</div>
                  {loan.campaign_description && (
                    <div className="text-xs text-green-700 mt-1">{loan.campaign_description}</div>
                  )}
                </div>
              )}
            </div>

            {/* Bank Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Min. Tutar</div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">
                  {loan.min_amount}
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Max. Tutar</div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">
                  {loan.max_amount}
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Faiz Oranı</div>
                <div className="font-semibold text-green-600 text-sm md:text-base">
                  {loan.interest_rate}
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Vade</div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">
                  {loan.min_term} - {loan.max_term} Ay
                </div>
              </div>
            </div>

            {/* Features */}
            {loan.features && loan.features.length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {loan.features.map((feature, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bank Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
                <Info size={16} className="text-[#F7BA45]" />
                <span>Başvuru için {loan.bank_name}'a yönlendirilirsiniz</span>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <a
                  id={`credit-loan-${loan.bank_id}-${loan.loan_type}`}
                  data-bank-name={loan.bank_name}
                  data-loan-type={loan.loan_type}
                  href={loan.redirect_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleApply(loan)}
                  className={`${gtmClasses[loan.bank_id] || ''} flex-1 sm:flex-none bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-4 md:px-6 py-2.5 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm md:text-base font-medium`}
                >
                  BAŞVUR
                  <ArrowRight size={16} className="hidden sm:block" />
                </a>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SponsoredBanks;